import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, updateAllHeaderCustomProperties } from '@theme/utilities';

const MEDIA_LAYER_SELECTORS = [
  '.hero__media-wrapper',
  '.sa-research-hero__background',
  '.sa-hero-banner__media-container',
  '.sa-hero-scroll__media-wrapper',
  '.slideshow__slide',
  '.layered-slideshow__panel',
  '.custom-section-background',
  '.background-image-container',
  'video-background-component',
  '.video-background',
  '[data-header-overlay-media]',
];

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isVisible(element) {
  if (!(element instanceof HTMLElement) || element.hidden) return false;

  const style = getComputedStyle(element);
  const rect = element.getBoundingClientRect();

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity) !== 0 &&
    rect.width > 1 &&
    rect.height > 1
  );
}

/**
 * @param {HTMLElement} layer
 * @returns {boolean}
 */
function layerHasVisibleMedia(layer) {
  if (getComputedStyle(layer).backgroundImage !== 'none') return true;

  return Array.from(layer.querySelectorAll('img, video')).some(isVisible);
}

/**
 * Detect a dominant image or video that starts at the top of the first content section.
 * @param {HTMLElement} section
 * @returns {boolean}
 */
function sectionStartsWithMedia(section) {
  const knownLayer = MEDIA_LAYER_SELECTORS.map((selector) => section.querySelector(selector)).find(
    (layer) => layer instanceof HTMLElement && isVisible(layer) && layerHasVisibleMedia(layer)
  );

  if (knownLayer) return true;

  const sectionRect = section.getBoundingClientRect();
  const minimumMediaHeight = Math.min(sectionRect.height * 0.5, Math.max(window.innerHeight * 0.25, 180));

  return Array.from(section.querySelectorAll('img, video')).some((media) => {
    if (!isVisible(media)) return false;

    const mediaRect = media.getBoundingClientRect();
    const mediaStyle = getComputedStyle(media);
    const fillsWidth = mediaRect.width >= sectionRect.width * 0.85;
    const startsAtTop =
      mediaRect.top <= sectionRect.top + 8 && mediaRect.bottom > sectionRect.top + minimumMediaHeight;
    const isBackgroundLayer =
      mediaStyle.objectFit === 'cover' ||
      mediaStyle.position === 'absolute' ||
      media.closest('[class*="background"], [class*="media-wrapper"], [class*="media-container"]') !== null;

    return fillsWidth && startsAtTop && mediaRect.height >= minimumMediaHeight && isBackgroundLayer;
  });
}

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Downward scroll distance accumulated before the header starts hiding.
   * @type {number}
   */
  #downwardScrollDistance = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /** @type {number | null} */
  #hideDelayTimeout = null;

  /** @type {number | null} */
  #hoverLeaveTimeout = null;

  /** @type {number | null} */
  #scrollUpIdleTimeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /** @constant {number} */
  #hideDelay = 500;

  /** @constant {number} */
  #hideScrollThreshold = 32;

  /** @constant {number} */
  #hoverLeaveDelay = 500;

  /** @constant {number} */
  #scrollUpIdleDelay = 500;

  #startHideTransition = () => {
    if (this.#hideDelayTimeout) {
      clearTimeout(this.#hideDelayTimeout);
      this.#hideDelayTimeout = null;
    }

    if (this.#timeout || this.dataset.stickyState === 'idle') return;

    this.setAttribute('data-animating', '');
    this.#timeout = setTimeout(() => {
      this.dataset.stickyState = 'idle'; // Hide
      this.removeAttribute('data-animating');
      this.#timeout = null;
    }, this.#animationDelay);
  };

  #cancelPendingHide = () => {
    this.#downwardScrollDistance = 0;

    if (this.#hideDelayTimeout) {
      clearTimeout(this.#hideDelayTimeout);
      this.#hideDelayTimeout = null;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (this.#scrollUpIdleTimeout) {
      clearTimeout(this.#scrollUpIdleTimeout);
      this.#scrollUpIdleTimeout = null;
    }

    this.removeAttribute('data-animating');
  };

  #scheduleScrollUpIdleHide = () => {
    if (this.#scrollUpIdleTimeout) clearTimeout(this.#scrollUpIdleTimeout);

    this.#scrollUpIdleTimeout = setTimeout(() => {
      this.#scrollUpIdleTimeout = null;

      if (this.matches(':hover') || this.hasAttribute('data-hover-grace')) return;

      this.#startHideTransition();
    }, this.#scrollUpIdleDelay);
  };

  #handlePointerEnter = () => {
    if (!window.matchMedia('(hover: hover)').matches) return;

    if (this.#hoverLeaveTimeout) {
      clearTimeout(this.#hoverLeaveTimeout);
      this.#hoverLeaveTimeout = null;
    }

    this.setAttribute('data-hover-grace', '');
    this.#cancelPendingHide();

    if (this.getAttribute('sticky') === 'scroll-up') {
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'none';
    }
  };

  #handlePointerLeave = () => {
    if (!window.matchMedia('(hover: hover)').matches) return;

    if (this.#hoverLeaveTimeout) return;

    if (this.#scrollUpIdleTimeout) {
      clearTimeout(this.#scrollUpIdleTimeout);
      this.#scrollUpIdleTimeout = null;
    }

    this.#hoverLeaveTimeout = setTimeout(() => {
      this.#hoverLeaveTimeout = null;

      if (this.getAttribute('sticky') !== 'scroll-up') {
        this.removeAttribute('data-hover-grace');
        return;
      }

      if ((document.scrollingElement?.scrollTop ?? 0) <= 0) {
        this.removeAttribute('data-scroll-transparent');
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        this.removeAttribute('data-hover-grace');
        return;
      }

      this.removeAttribute('data-hover-grace');
      this.#startHideTransition();
    }, this.#hoverLeaveDelay);
  };

  /**
   * Reveals a hidden scroll-up header when the pointer reaches its viewport position.
   * @param {PointerEvent} event
   */
  #handleDocumentPointerMove = (event) => {
    const isHoverDevice = event.pointerType === 'mouse' && window.matchMedia('(hover: hover)').matches;
    const activationHeight = this.offsetHeight;

    if (
      isHoverDevice &&
      this.hasAttribute('data-hover-grace') &&
      !this.matches(':hover') &&
      event.clientY > activationHeight
    ) {
      this.#handlePointerLeave();
      return;
    }

    if (
      !isHoverDevice ||
      this.getAttribute('sticky') !== 'scroll-up' ||
      this.dataset.stickyState !== 'idle' ||
      (document.scrollingElement?.scrollTop ?? 0) <= 0 ||
      event.clientY > activationHeight
    ) {
      return;
    }

    this.#cancelPendingHide();
    this.setAttribute('data-scroll-transparent', '');
    this.setAttribute('data-hover-grace', '');
    this.dataset.stickyState = 'active';
    this.dataset.scrollDirection = 'none';
  };

  /** @type {string | null} */
  #serverTransparentMode = null;

  /** @type {number | null} */
  #presentationFrame = null;

  #syncNavigationColors = () => {
    const headerStyle = getComputedStyle(this);
    const menus = Array.from(this.querySelectorAll('header-menu')).filter(
      (element) => element instanceof HTMLElement
    );
    const pageStyle = Array.from(document.querySelectorAll('[data-page-navigation-style]')).find((element) => {
      if (!(element instanceof HTMLElement)) return false;

      const configuredPath = element.dataset.pagePath?.trim();
      if (!configuredPath) return true;

      try {
        const configuredUrlPath = new URL(configuredPath, window.location.origin).pathname.replace(/\/$/, '');
        const currentUrlPath = window.location.pathname.replace(/\/$/, '');

        return configuredUrlPath === currentUrlPath;
      } catch {
        return false;
      }
    });
    const properties = [
      ['--navigation-background-color', '--navigation-global-background-color'],
      ['--navigation-foreground-color', '--navigation-global-foreground-color'],
      ['--navigation-foreground-rgb', '--navigation-global-foreground-rgb'],
      ['--navigation-transparent-background-color', '--navigation-global-transparent-background-color'],
      ['--navigation-transparent-foreground-color', '--navigation-global-transparent-foreground-color'],
      ['--navigation-transparent-foreground-rgb', '--navigation-global-transparent-foreground-rgb'],
      ['--navigation-transparent-logo-color', '--navigation-global-transparent-logo-color'],
    ];

    for (const [property, globalProperty] of properties) {
      const pageValue = pageStyle instanceof HTMLElement ? pageStyle.style.getPropertyValue(property).trim() : '';
      const value = pageValue || headerStyle.getPropertyValue(globalProperty).trim();
      if (!value) continue;

      this.style.setProperty(property, value);
      for (const menu of menus) {
        menu.style.setProperty(property, value);
      }
    }

    const optionalPageProperties = [
      '--navigation-contact-background-color',
      '--navigation-contact-foreground-color',
    ];

    for (const property of optionalPageProperties) {
      const value = pageStyle instanceof HTMLElement ? pageStyle.style.getPropertyValue(property).trim() : '';

      if (value) {
        this.style.setProperty(property, value);
      } else {
        this.style.removeProperty(property);
      }
    }

    this.toggleAttribute(
      'data-hide-primary-navigation',
      pageStyle instanceof HTMLElement && pageStyle.hasAttribute('data-hide-primary-navigation')
    );
  };

  #updateMediaTransparency = () => {
    const main = document.querySelector('#MainContent');
    if (!(main instanceof HTMLElement)) return;

    const firstSection = Array.from(main.children).find(isVisible);
    const isOverMedia = firstSection instanceof HTMLElement && sectionStartsWithMedia(firstSection);
    const sectionOwnsHeaderOverlap =
      firstSection instanceof HTMLElement && firstSection.querySelector('[data-header-overlap-owner]') !== null;

    this.toggleAttribute('data-over-media', isOverMedia);
    this.toggleAttribute('data-section-managed-overlap', sectionOwnsHeaderOverlap);

    if (isOverMedia && !sectionOwnsHeaderOverlap) {
      this.setAttribute('transparent', this.hasAttribute('sticky') ? 'not-sticky' : 'always');

      if ((document.scrollingElement?.scrollTop ?? 0) > 0 && this.hasAttribute('sticky')) {
        this.dataset.stickyState = 'active';
      }
    } else if (this.#serverTransparentMode) {
      this.setAttribute('transparent', this.#serverTransparentMode);
    } else {
      this.removeAttribute('transparent');
    }

    document.body.classList.toggle('header-transparent', this.hasAttribute('transparent'));
    updateAllHeaderCustomProperties();
  };

  #schedulePresentationSync = () => {
    if (this.#presentationFrame !== null) cancelAnimationFrame(this.#presentationFrame);

    this.#presentationFrame = requestAnimationFrame(() => {
      this.#presentationFrame = null;
      this.#syncNavigationColors();
      this.#updateMediaTransparency();
    });
  };

  syncPresentation() {
    this.#schedulePresentationSync();
  }

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('header--desktop-hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('header--desktop-hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.toggleAttribute('data-at-top', scrollTop <= 0);

    const stickyMode = this.getAttribute('sticky');

    // Standard check: if offscreen is false AND stickyMode is not 'always', we generally don't care.
    // BUT if it is 'scroll-up', we DO care even if offscreen is false initially to detect the first scroll down.
    if (!this.#offscreen && stickyMode !== 'always' && stickyMode !== 'scroll-up') return;

    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = scrollDelta < 0;

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'inactive';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
        this.dataset.stickyState = 'active';
      } else {
        this.dataset.scrollDirection = 'down';
        this.dataset.stickyState = 'active';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // Hover takes priority over scroll-down hiding. Keep the header sticky and solid
    // while the pointer remains inside it, then start a fresh threshold after pointer leave.
    if (stickyMode === 'scroll-up' && window.matchMedia('(hover: hover)').matches && this.matches(':hover')) {
      this.#cancelPendingHide();
      if (scrollTop > 0) this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      return;
    }

    // Scroll-up logic
    if (isScrollingUp) {
      this.#cancelPendingHide();

      if (scrollTop <= 0) {
        // At top: reset
        this.removeAttribute('data-scroll-transparent');
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // Scrolling up mid-page: reveal the solid sticky header immediately.
        this.removeAttribute('data-scroll-transparent');
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        this.#scheduleScrollUpIdleHide();
      }
    } else if (scrollTop > 0) { 
      // Scrolling down
      if (this.#scrollUpIdleTimeout) {
        clearTimeout(this.#scrollUpIdleTimeout);
        this.#scrollUpIdleTimeout = null;
      }

      this.removeAttribute('data-scroll-transparent');
      this.#downwardScrollDistance += Math.max(scrollDelta, 0);

      if (this.dataset.stickyState === 'active' || this.dataset.stickyState === 'inactive' || !this.dataset.stickyState) {
        this.dataset.scrollDirection = 'none';

        if (this.#downwardScrollDistance < this.#hideScrollThreshold) {
          this.#lastScrollTop = scrollTop;
          return;
        }

        if (this.#hideDelayTimeout || this.#timeout) {
           this.#lastScrollTop = scrollTop;
           return;
        }

        this.#hideDelayTimeout = setTimeout(() => {
          this.#hideDelayTimeout = null;
          this.#startHideTransition();
        }, this.#hideDelay);
      }
    } else {
      // At top (scrollTop <= 0)
      this.#cancelPendingHide();
      this.removeAttribute('data-scroll-transparent');
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'inactive';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#serverTransparentMode = this.getAttribute('transparent');
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.toggleAttribute('data-at-top', this.#lastScrollTop <= 0);
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    document.addEventListener('pointermove', this.#handleDocumentPointerMove);
    document.addEventListener('scroll', this.#handleWindowScroll);
    window.addEventListener('resize', this.#schedulePresentationSync);
    this.#schedulePresentationSync();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#presentationFrame !== null) cancelAnimationFrame(this.#presentationFrame);
    if (this.#hideDelayTimeout) clearTimeout(this.#hideDelayTimeout);
    if (this.#hoverLeaveTimeout) clearTimeout(this.#hoverLeaveTimeout);
    if (this.#scrollUpIdleTimeout) clearTimeout(this.#scrollUpIdleTimeout);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    document.removeEventListener('pointermove', this.#handleDocumentPointerMove);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#schedulePresentationSync);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const syncCurrentHeader = () => {
      const currentHeader = headerGroup.querySelector('header-component');
      if (currentHeader instanceof HeaderComponent) currentHeader.syncPresentation();
    };

    const ensureMediaTransparency = () => {
      const currentHeader = headerGroup.querySelector('header-component');
      if (
        !(currentHeader instanceof HTMLElement) ||
        !currentHeader.hasAttribute('data-over-media') ||
        currentHeader.hasAttribute('data-section-managed-overlap') ||
        currentHeader.hasAttribute('transparent')
      ) {
        return;
      }

      currentHeader.setAttribute('transparent', currentHeader.hasAttribute('sticky') ? 'not-sticky' : 'always');
      document.body.classList.add('header-transparent');
      updateAllHeaderCustomProperties();
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldSyncNavigationColors = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          shouldSyncNavigationColors = true;

          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        } else if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.matches('[data-page-navigation-style]')
        ) {
          shouldSyncNavigationColors = true;
        }
      }

      if (shouldSyncNavigationColors) syncCurrentHeader();
      ensureMediaTransparency();
    });

    mutationObserver.observe(headerGroup, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-over-media',
        'data-section-managed-overlap',
        'transparent',
        'data-page-path',
        'style',
      ],
    });
    document.addEventListener('shopify:section:load', syncCurrentHeader);
    document.addEventListener('shopify:section:unload', syncCurrentHeader);
    document.addEventListener('shopify:section:reorder', syncCurrentHeader);
    ensureMediaTransparency();
    syncCurrentHeader();
  }
});
